/**
 * Shared animation presets for use with motion/react across the app.
 *
 * All presets are Lenis-safe: they use CSS opacity/transform on mount/unmount
 * or IntersectionObserver (whileInView). None sample the scroll position directly.
 *
 * Deliberately excludes useScroll — if needed, read lenisRef.current.scroll
 * instead of the native scroll offset to avoid jitter with the Lenis RAF loop.
 *
 * Easing curves:
 *   decelerate [0, 0, 0.2, 1]  — elements entering (most UI transitions)
 *   standard   [0.4, 0, 0.2, 1] — general state transitions
 *   accelerate [0.4, 0, 1, 1]   — elements leaving
 */

const decelerate = [0, 0, 0.2, 1] as const;
const accelerate = [0.4, 0, 1, 1] as const;

/**
 * Fade-in for top-level page transitions. No y movement (avoids the jarring
 * "whole layout slides up" effect on pages with structural sidebars) and no
 * exit animation (avoids the double-flash from sequential fade-out → fade-in).
 * The old page disappears instantly; the new page fades in.
 */
export const pageFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.15, ease: decelerate },
} as const;

/**
 * Fade up on enter, fade up-out on exit. Use for content panels and detail
 * views that live inside a page — not for the page wrapper itself.
 */
export const fadeInUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.22, ease: decelerate },
} as const;

/**
 * Motion container that staggers its children. Uses the variants system so
 * the parent propagates `initial`/`animate` state names to children.
 * Pair with `staggerItem` on each child. Wrap children in `AnimatePresence
 * initial={false}` so items already present on first render don't animate.
 */
export const staggerContainer = {
  initial: 'initial',
  animate: 'animate',
  variants: {
    initial: {},
    animate: { transition: { staggerChildren: 0.035 } },
  },
} as const;

/**
 * Individual item inside a `staggerContainer`. Inherits `initial`/`animate`
 * via variant propagation. Exit animation uses a direct prop so
 * `AnimatePresence` can remove items cleanly.
 */
export const staggerItem = {
  variants: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.14, ease: decelerate } },
  },
  exit: { opacity: 0, transition: { duration: 0.1, ease: accelerate } },
} as const;

/** Subtle press and hover effect for clickable cards and interactive elements. */
export const microPress = {
  whileHover: { scale: 1.01 },
  whileTap: { scale: 0.98 },
  transition: { type: 'spring', stiffness: 500, damping: 30 },
} as const;
